import * as React from 'react';

import Dropdown from 'af-components/Controls/Dropdown';

import styles from './styles.module.scss';

const containerStyle = [
	styles.additionalFilter,
	styles.bulkModalStyle,
].join(' ');

interface MultiSelectFilterOption {
	id: number | null | string;
	label: string;
}

interface OwnProps {
	options: MultiSelectFilterOption[] | null;
	onChange: (options: MultiSelectFilterOption[]) => void;
	selected: MultiSelectFilterOption[];
	defaultValue?: number;
	/** Define how to call the filtering option will be referred to */
	filterName: string;
	onRemoveFilter?: () => void;
	/** Should the dropdown menu be opened immediately? */
	autoOpen: boolean;
	/** Enabling this adds "None" as the first option on the list. Will send "null" as requested value of the filter */
	hasNoneAsOption?: true;
	/** Define how you'd like to display a single option within the multiselect dropdown */
	renderOptionOverride?: (option: MultiSelectFilterOption) => React.JSX.Element;
	onLazyLoad?: (loaded?: boolean) => Promise<void>;
	selectBtnLabel?: string;
	label?: string;
	placeholder: string;
}

type Props = OwnProps;

const MultiSelectDropdown: React.FC<Props> = (props) => {
	const {
		options,
		onChange,
		defaultValue,
		selected,
		filterName,
		onRemoveFilter,
		autoOpen,
		hasNoneAsOption,
		renderOptionOverride,
		onLazyLoad,
		selectBtnLabel,
		label,
		placeholder,
	} = props;

	const allOptions = React.useMemo(() => {
		if (options === null) {
			// we want to make sure we're displaying loading indicator
			return null;
		} else if (hasNoneAsOption) {
			const noneAsOption: MultiSelectFilterOption = { id: null, label: 'N/A' };
			return [noneAsOption, ...(options ?? [])];
		} else {
			return options;
		}
	}, [options, hasNoneAsOption]);

	const renderOption = React.useCallback((option: MultiSelectFilterOption) => {
		const isNoneAsOption = option.id === null;

		const className = isNoneAsOption
			? `${styles.dropdownOption} ${styles.noneOption}`
			: styles.dropdownOption;

		return (
			<div className={className}>
				{
					!!renderOptionOverride
						? renderOptionOverride(option)
						: option.label
				}
			</div>
		);
	}, [renderOptionOverride]);

	const onValueChange = React.useCallback((_options: MultiSelectFilterOption[]) => {
		onChange(_options);
	}, [onChange]);

	const defaultOption = React.useMemo(() => {
		if (!allOptions) {
			return null;
		}

		const _option = allOptions.find((option) => option.id === defaultValue);
		return _option ?? allOptions[0];
	}, [defaultValue, allOptions]);

	const placeholderCalculated = React.useMemo(() => {
		if (selected.length === 0) {
			return <div className={styles.emptyFilterContent}>{placeholder}</div>;
		}

		const [first, ...rest] = selected;
		return (
			<div className={styles.appliedFilterContent}>
				{first?.label ?? 'Empty'}
				{rest.length > 0 && (
					<div className={styles.appliedFilterCounter}>
						{` + ${rest.length}`}
					</div>
				)}
			</div>
		);
	}, [selected, placeholder]);

	const filterBy = React.useCallback((option: MultiSelectFilterOption, rawInput: string) => {
		return option.label.toLowerCase().includes(rawInput.toLowerCase());
	}, []);

	return (
		<Dropdown<MultiSelectFilterOption>
			autoOpen={autoOpen}
			containerClassName={containerStyle}
			defaultSelections={selected}
			defaultValue={defaultOption}
			dropdownId={`accqa__additional-filter-multiselect__${filterName}`}
			filterable={true}
			filterBy={filterBy}
			fixed={true}
			forcePlaceholder={true}
			isMultiselect={true}
			label={label}
			onClose={onRemoveFilter}
			onLazyLoad={onLazyLoad}
			onMultiselectChange={onValueChange}
			options={allOptions}
			placeholder={placeholderCalculated}
			renderMenuItem={renderOption}
			selectBtnLabel={selectBtnLabel}
			valueKey="id"
			withCaret={true}
		/>
	);
};

export default React.memo(MultiSelectDropdown);
